<template>
  <g class="cluster-point-vector">
    <defs>
      <linearGradient :id="'grad' + uuid" :gradientTransform="'rotate(' + compAngle + ' 0.5 0.5)'">
        <stop offset="0%" :stop-color="grad[0]"/>
        <stop offset="100%" :stop-color="grad[1]"/>
      </linearGradient>
    </defs>

    <circle :cx="cx" :cy="cy" :r="r" :fill="'url(#grad' + uuid + ')'"/>

    <text
      fill="#FFFFFF"
      :y="txtY"
      :x="txtX"
      :style="[ { lineHeight: 0.85 }, activated ? { fontSize: fSize + 'px' } : { fontSize: data.fontSize + 'px' } ]"
      ref="txt"
    >
      <tspan class="scatter-num" dominant-baseline="hanging">
        <tspan
          class="knockout-50 num"
          dominant-baseline="hanging"
          text-anchor="middle"
          :dx="isMS ? 5 : txtRelFactor * 0.25"
        >{{ Math.ceil(num) }}</tspan>
        <tspan
          class="knockout-30 percent-sign"
          dominant-baseline="hanging"
          :dx="isMS ? (-0.2 * (fSize / 24)) + 'em' : (-0.2 * (fSize / 24)) + 'em'"
          :dy="isMS ? -0.4 * fSize : '0.25em'"
          style="font-size: 24px">%
        </tspan>
        <tspan
          class="adobe-clean-black label"
          dominant-baseline="hanging"
          text-anchor="middle"
          style="font-size: 1rem"
          v-for="(line, idx) in title"
          :x="cx"
          :dy="labelDy(idx)"
        >{{ line }}</tspan>
      </tspan>
    </text>
  </g>
</template>

<script>
  import { TweenMax, Elastic } from 'gsap'
  import { dataTransTime } from '@/lib/consts'
  import { gradPtColor } from '@/lib/funcs'
  import GraphFragMixin from '@/components/report/mixins/GraphFragMixin'

  export default {
    name: 'ClusterFragment',
    mixins: [ GraphFragMixin ],
    props: {
      data: { type: Object, required: true },
      bg: { type: String, required: true },
      idx: { type: Number, required: true },
      angle: { type: Number, default () { return 90 } }
    },
    data () {
      return {
        cx: this.data.cx,
        cy: this.data.cy,
        r: 0,
        fSize: 0,
        gradAngle: this.angle,
        tweenDelay: this.idx * 0.15,
        activated: false
      }
    },
    watch: {
      data ( { cx, cy, r, fontSize, num } ) {
        this.handleChange( { cx, cy, r, num, fSize: fontSize, gradAngle: this.angle }, dataTransTime * 0.75 )
      },
      active ( val ) {
        if ( val ) {
          let { cx, cy, r, fontSize, num } = this.data
          this.handleChange( { cx, cy, r, num, fSize: fontSize, gradAngle: this.angle } )
        }
      }
    },
    computed: {
      size () {
        return this.r * 2
      },
      grad () {
        switch ( true ) {
          case (this.bg === this.mainColor && this.data.color === 'mainColor'):
            return [ this.mainColor, gradPtColor( ...this.colors, 0.7 ) ]

          case (this.bg === this.accent && this.data.color === 'mainColor'):
            return [ gradPtColor( ...this.colorsRev, 0.1 ), gradPtColor( ...this.colorsRev, 0.5 ) ]

          case (this.bg === this.accent && this.data.color === 'accent'):
            return [ gradPtColor( ...this.colors, 0.1 ), gradPtColor( ...this.colors, 0.45 ) ]

          case (this.bg === this.mainColor && this.data.color === 'both'):
            return [ this.mainColor, gradPtColor( ...this.colors, 0.9 ) ]

          default:
            return this.colors
        }
      },
      compAngle () {
        return this.gradAngle === -90 && this.bg === this.accent && this.data.color === 'mainColor' ? 90 : this.gradAngle
      },
      txtRelFactor () {
        return this.activated ? this.size : this.data.r * 2
      },
      txtY () {
        switch ( true ) {
          case this.isFF:
            return this.cy - (this.txtRelFactor * 0.35)

          case this.isMS:
            let fontVal = this.activated ? this.fSize : this.fontSize
            return (this.cy - (this.txtRelFactor * 0.275)) + (fontVal * 0.85)

          default:
            return this.cy - (this.txtRelFactor * 0.235)
        }
      },
      txtX () {
        return this.isMS ? this.cx : this.cx - (this.txtRelFactor * 0.225)
      },
      title () {
        return this.label.title.split( '<br>' )
      }
    },
    methods: {
      labelDy ( idx ) {
        let multiplier = this.isMS ? 0.5 : 0.8
        let correction = this.isMS ? 16 : 0

        return idx === 0 && this.activated
          ? this.fSize * multiplier + correction
          : idx === 0
            ? this.data.fontSize * multiplier + correction
            : 16
      },
      handleChange ( obj, time = dataTransTime ) {
        if ( typeof obj === 'object' ) {
          let { num, fSize, ...elastic } = obj
          TweenMax.to( this, time, { ...elastic, ease: Elastic.easeOut.config( 0.8, 0.4 ), delay: this.tweenDelay } )
          TweenMax.to( this, time / 2, {
            num: num,
            fSize: fSize, // pls don't delete
            delay: this.tweenDelay,
            onComplete: () => {
              if ( this.active ) {
                this.activated = true
                this.$emit( 'activated' )
              }
            }
          } )
          if ( !this.activated ) {
            TweenMax.to( this.$refs.txt, time / 2, { transformOrigin: '50% 50%', scale: 1, delay: this.tweenDelay } )
          }
        }
      }
    },
    mounted () {
      TweenMax.set( this.$refs.txt, { transformOrigin: '50% 50%', scale: 0 } )
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../../assets/scss/global/the_goods";

  .label {
    letter-spacing : 0.11125em;
  }
</style>



// WEBPACK FOOTER //
// src/components/report/content/ClusterFragment.vue